// Future-organism IDENTITY — the one derivation a real user's next-gen
// organism is born from. Pure function of who they are (identity string) and
// how much they remember (cells): same inputs, same organism, everywhere.
//
// Composition:
//  · seed      — FNV-1a of the normalized identity (same seed as avatars/marks)
//  · color     — the curated mint (organismFromSeed), then a VIVID pass: the
//                future engine's watercolour-transmittance render meets the
//                colour philosophy of the reference board (punchy saturated
//                pigment, never washed) by lifting the pins' chroma.
//  · tuners    — futureTunersFromSeed: mint-safe bands only (no high cellSize,
//                no low radialBias — the combinations that expose petal roots).
//  · age       — ageFromMemory(cells): the memory law, never time.

import type { ColorState } from "../color";
import { identityKey, organismFromSeed, seedFromString } from "../identity";
import { ageFromMemory } from "../macro-tuners";
import { futureTunersFromSeed, type FutureTuners } from "./futureOrganism";

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Lift a curated-mint pin into the future organism's punchy band — saturation
 *  up, a touch brighter, hue untouched. The curated wheel keeps taste; the
 *  vivid pass gives the transmittance render real pigment to pool. */
function vividPin(p: { hue: number; sat: number; light: number }) {
  return {
    hue: p.hue,
    sat: clamp(p.sat + 0.24, 0.62, 0.95),
    light: clamp(p.light + 0.04, 0.5, 0.63),
  };
}

/** Lift ANY ColorState into the future engine's punchy pigment band — used by
 *  product mounts so the theme's curated (muted) pins render with real pigment
 *  while the UI tint keeps reading the original values. */
export function vividColorState(c: ColorState): ColorState {
  return {
    primary: vividPin(c.primary),
    secondary: vividPin(c.secondary),
    ...(c.tinge ? { tinge: vividPin(c.tinge) } : {}),
    contrast: clamp(c.contrast + 0.06, 0.6, 0.85),
  };
}

export type FutureIdentity = {
  seed: number;
  color: ColorState;
  tuners: Partial<FutureTuners>;
  age: number;
};

/** The CURATED (un-vivid) palette a future identity is born from — the same
 *  per-seed hue rotation as futureIdentity, before the vivid pass. A product
 *  mount hands THIS to the theme (the UI tint reads curated pins) and the engine
 *  applies its own vivid pass at render — so a newborn minted on the birth page
 *  and the same identity seated under the dashboard are one creature. */
export function futurePalette(identity: string): ColorState {
  const seed = seedFromString(identityKey(identity));
  const minted = organismFromSeed(identity);
  // DIFFERENTIATION — two neighbours can land on the same curated palette and
  // read as near-twins. A deterministic per-seed hue ROTATION (±34°, preserving
  // the harmony's relative offsets) spreads the population across the wheel
  // while every individual stays inside the tasteful band.
  const rot = (((seed >>> 5) % 69) - 34);
  const spin = (p: { hue: number; sat: number; light: number }) => ({
    ...p,
    hue: (((p.hue + rot) % 360) + 360) % 360,
  });
  return {
    primary: spin(minted.primary),
    secondary: spin(minted.secondary),
    ...(minted.tinge ? { tinge: spin(minted.tinge) } : {}),
    contrast: minted.contrast,
  };
}

export function futureIdentity(identity: string, cells: number): FutureIdentity {
  const seed = seedFromString(identityKey(identity));
  const pal = futurePalette(identity);
  return {
    seed,
    color: {
      primary: vividPin(pal.primary),
      secondary: vividPin(pal.secondary),
      ...(pal.tinge ? { tinge: vividPin(pal.tinge) } : {}),
      contrast: clamp(pal.contrast + 0.06, 0.6, 0.85),
    },
    tuners: futureTunersFromSeed(seed),
    age: ageFromMemory(cells),
  };
}
